export type TBetResultType = "won" | "lost" | "returned" | "canceled";

export interface BetResultPillData {
  id: string;
  type: TBetResultType;
  amount: string;
  price: number;
  closePriceTs: number; // Unix ms — used to find matching candle by timestamp
}

const CONFIGS: Record<TBetResultType, { bg: string; border: string | null; iconBg: string; iconColor: string }> = {
  won: { bg: "#27272A", border: "#17C964", iconBg: "#17C964", iconColor: "#fff" },
  lost: { bg: "#27272A", border: "#F31260", iconBg: "#F31260", iconColor: "#fff" },
  returned: { bg: "#27272A", border: "#F4A425", iconBg: "#F4A425", iconColor: "#fff" },
  canceled: { bg: "#27272A", border: "#F31260", iconBg: "#F31260", iconColor: "#fff" },
};

const PILL_H = 26;
const PAD_H = 10;
const GAP = 2;
const FONT_SIZE = 14;
const ICON_SIZE = 12;
const ICON_R = ICON_SIZE / 2;
const OFFSET_ABOVE = 42;
const TAIL_H = 8;
const TAIL_HALF_W = 7;

export function drawBetResultPill(
  ctx: CanvasRenderingContext2D,
  cx: number,
  priceY: number,
  type: TBetResultType,
  amount: string,
) {
  const cfg = CONFIGS[type] ?? CONFIGS.lost;

  ctx.save();
  ctx.font = `700 ${FONT_SIZE}px Arial, sans-serif`;
  const textW = ctx.measureText(amount).width;
  const pillW = Math.max(44, Math.ceil(PAD_H + textW + GAP + ICON_SIZE + PAD_H));

  const pillX = cx - pillW / 2;
  const pillY = priceY - PILL_H - OFFSET_ABOVE;

  // Background
  ctx.beginPath();
  (ctx as any).roundRect(pillX, pillY, pillW, PILL_H, 12);
  ctx.fillStyle = cfg.bg;
  ctx.fill();

  // Border
  if (cfg.border) {
    ctx.beginPath();
    (ctx as any).roundRect(pillX + 0.5, pillY + 0.5, pillW - 1, PILL_H - 1, 12);
    ctx.strokeStyle = cfg.border;
    ctx.lineWidth = 1;
    ctx.stroke();
  }

  if (cfg.border) {
    ctx.beginPath();
    ctx.moveTo(cx - TAIL_HALF_W, pillY + PILL_H - 1);
    ctx.lineTo(cx + TAIL_HALF_W, pillY + PILL_H - 1);
    ctx.lineTo(cx, pillY + PILL_H + TAIL_H);
    ctx.closePath();
    ctx.fillStyle = cfg.border;
    ctx.fill();
  }

  // Amount text
  ctx.fillStyle = "#fff";
  ctx.textBaseline = "middle";
  ctx.textAlign = "left";
  ctx.font = `700 ${FONT_SIZE}px Arial, sans-serif`;
  const midY = pillY + PILL_H / 2;
  ctx.fillText(amount, pillX + PAD_H, midY);

  // Icon circle
  const circleX = pillX + PAD_H + textW + GAP + ICON_R;
  const circleY = midY;
  ctx.beginPath();
  ctx.arc(circleX, circleY, ICON_R, 0, Math.PI * 2);
  ctx.fillStyle = cfg.iconBg;
  ctx.fill();

  // Icon symbol
  ctx.strokeStyle = cfg.iconColor;
  ctx.lineWidth = 1.4;
  ctx.lineCap = "round";
  ctx.lineJoin = "round";
  const s = ICON_R * 0.42;

  if (type === "won") {
    ctx.beginPath();
    ctx.moveTo(circleX - s, circleY);
    ctx.lineTo(circleX - s * 0.15, circleY + s);
    ctx.lineTo(circleX + s, circleY - s * 0.75);
    ctx.stroke();
  } else if (type === "lost" || type === "canceled") {
    ctx.beginPath();
    ctx.moveTo(circleX - s, circleY - s);
    ctx.lineTo(circleX + s, circleY + s);
    ctx.moveTo(circleX + s, circleY - s);
    ctx.lineTo(circleX - s, circleY + s);
    ctx.stroke();
  } else {
    // Circular arrow (returned)
    ctx.lineWidth = 1.4;
    ctx.beginPath();
    ctx.arc(circleX, circleY, s, -Math.PI * 0.75, Math.PI * 0.6, false);
    ctx.stroke();
    const endAngle = Math.PI * 0.6;
    const ex = circleX + Math.cos(endAngle) * s;
    const ey = circleY + Math.sin(endAngle) * s;
    const ah = s * 0.45;
    ctx.beginPath();
    ctx.moveTo(ex - ah, ey - ah * 0.3);
    ctx.lineTo(ex, ey + ah * 0.7);
    ctx.lineTo(ex + ah * 0.3, ey - ah);
    ctx.stroke();
  }

  ctx.restore();
}
